import { useMemo } from 'react'
import { addDays, format, parseISO } from 'date-fns'
import { useAuth } from '../../../hooks/useAuth'
import { useLinkedHouseholds } from '../../linkedHouseholds/hooks/useLinkedHouseholds'
import { useCustodyCalendar } from '../hooks/useCustody'

export const UpcomingTransitions = ({ schedules }) => {
  const { household } = useAuth()
  const { activeLink } = useLinkedHouseholds()

  const linkedHouseholdName = activeLink?.otherHousehold?.name ?? 'Other House'
  const ourName             = household?.name ?? 'Your House'

  const today = useMemo(() => new Date(), [])
  const endDate = useMemo(() => addDays(today, 14), [today])

  const { data: calendarData, isLoading } = useCustodyCalendar(
    schedules?.filter((s) => s.is_active),
    today,
    endDate
  )

  const transitions = useMemo(() => {
    if (!calendarData || !schedules?.length) return []
    const result = []

    schedules.filter((s) => s.is_active).forEach((schedule) => {
      let prevHH = null
      let cur = today
      while (cur <= endDate) {
        const dateStr = format(cur, 'yyyy-MM-dd')
        const hhId = calendarData[dateStr]?.[schedule.id] ?? null
        if (hhId && hhId !== prevHH && prevHH !== null) {
          result.push({
            date: new Date(cur),
            schedule,
            fromHH: prevHH === household?.id ? ourName : linkedHouseholdName,
            toHH:   hhId   === household?.id ? ourName : linkedHouseholdName,
            direction: hhId === household?.id ? 'coming' : 'going',
          })
        }
        prevHH = hhId
        cur = addDays(cur, 1)
      }
    })

    return result.sort((a, b) => a.date - b.date).slice(0, 7)
  }, [calendarData, schedules, household, ourName, linkedHouseholdName, today, endDate])

  if (isLoading || transitions.length === 0) return null

  return (
    <div className="mb-4">
      <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">
        Upcoming Transitions
      </h2>
      <div className="bg-surface rounded-base shadow-card overflow-hidden">
        {transitions.map((t, i) => (
          <div
            key={`${t.schedule.id}-${t.date.toISOString()}`}
            className={`flex items-center gap-3 px-4 py-3 ${i < transitions.length - 1 ? 'border-b border-border' : ''}`}
          >
            <div
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: t.schedule.color }}
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-text-primary truncate">
                {t.schedule.child?.display_name}
              </p>
              <p className="text-xs text-text-muted">
                {t.direction === 'coming' ? '← Coming to' : '→ Going to'} {t.toHH}
              </p>
            </div>
            <span className="text-xs text-text-muted flex-shrink-0">
              {format(t.date, 'EEE, MMM d')}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
